import { rem } from 'polished'
import React, { ComponentProps, forwardRef } from 'react'
import styled from 'styled-components'
import BodyText from '../Typography/BodyText'
import RadioButton from './RadioButton'
import clsx from 'clsx'

const Label = styled.label<{ $fullWidth?: boolean, $labelAlign: 'center' | 'start' }>`
  display: ${props => props.$fullWidth ? 'flex' : 'inline-flex'};
  align-items: ${props => props.$labelAlign};
  gap: ${rem(12)};
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
`

const RadioText = styled(BodyText)`
  flex-grow: 1;
  transition: color 0.2s;
`

export type RadioInputTextSize = 'small' | 'medium' | 'large'

interface Props extends ComponentProps<typeof RadioButton> {
  /**
   * Vertical alignment of the radio button relative to its label content.
   * Use `start` when the label spans multiple lines.
   * @default center
   */
  labelAlign?: 'center' | 'start'
  /**
   * Whether the radio row should stretch to the full container width.
   * @default false
   */
  fullWidth?: boolean
  /**
   * Size of the label text, for when it shouldn't match the radio button.
   * @default the value of `size`
   */
  textSize?: RadioInputTextSize
}

/**
 * Radio input with a label.
 *
 * For the radio input without the label see `<RadioButton>`
 */
const RadioInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { children, className, size = 'medium', labelAlign = 'center', fullWidth = false, textSize, ...inputProps } = props
  const hasChildren = React.Children.toArray(children).length !== 0

  return <Label className={clsx(className, `size-${size}`)} htmlFor={inputProps.id} $labelAlign={labelAlign} $fullWidth={fullWidth}>
    <RadioButton {...inputProps} ref={ref} size={size} />
    {hasChildren && <RadioText
      colour={inputProps.disabled ? 'neutral-four' : 'neutral-one'}
      variant={textSize ?? size}
      forwardedAs="span"
    >
      {children}
    </RadioText>}
  </Label>
})

export default RadioInput
